/**
 * GroupDiningPage — Iter 195.
 *
 * Unified page for both the **host** (post-login) and **invitees**
 * (from tokenized email link). Renders:
 *
 *   - Header with restaurant, booking time, party size
 *   - RSVP controls (invitees only)
 *   - Attendee list with dietary badges
 *   - Live map (activates T-30 → T+30)
 *   - Group chat
 *   - "Running late" one-tap
 *   - Host-only: checkout with mode selector
 *
 * URL patterns:
 *   /group-dining/:gid                         (host or logged-in attendee)
 *   /group-dining/:gid/invite?t=<sharetoken>&a=<attendee_id>  (from email)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL || "";

function useTokenParams() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  return {
    t: params.get("t") || "",
    a: params.get("a") || "",
  };
}

function apiParams(t, a) {
  const q = [];
  if (t) q.push(`t=${encodeURIComponent(t)}`);
  if (a) q.push(`a=${encodeURIComponent(a)}`);
  return q.length ? `?${q.join("&")}` : "";
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function GroupDiningPage() {
  const { gid } = useParams();
  const { t, a } = useTokenParams();

  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [chat, setChat] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const [mapData, setMapData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [calLinks, setCalLinks] = useState(null);
  const chatBoxRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/group-dining/${encodeURIComponent(gid)}${apiParams(t, a)}`,
      );
      setState({ loading: false, error: "", data });
    } catch (e) {
      setState({
        loading: false,
        data: null,
        error: formatApiError(e.response?.data?.detail) || e.message,
      });
    }
  }, [gid, t, a]);

  const loadChat = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/group-dining/${encodeURIComponent(gid)}/chat${apiParams(t, a)}`,
      );
      setChat(data?.messages || []);
    } catch { /* silent */ }
  }, [gid, t, a]);

  const loadMap = useCallback(async () => {
    if (!state.data?.live_map_open) return;
    try {
      const { data } = await api.get(
        `/group-dining/${encodeURIComponent(gid)}/map${apiParams(t, a)}`,
      );
      setMapData(data);
    } catch { /* silent */ }
  }, [gid, t, a, state.data?.live_map_open]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadChat(); }, [loadChat]);
  useEffect(() => { loadMap(); }, [loadMap]);

  // Poll chat + map every 20s while live
  useEffect(() => {
    const id = setInterval(() => {
      loadChat();
      if (state.data?.live_map_open) loadMap();
    }, 20000);
    return () => clearInterval(id);
  }, [loadChat, loadMap, state.data?.live_map_open]);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [chat.length]);

  const submitRsvp = async (rsvp) => {
    setBusy(true);
    try {
      await api.post(
        `/group-dining/${encodeURIComponent(gid)}/rsvp${apiParams(t, a)}`,
        { rsvp, location_opt_in: rsvp === "accepted" },
      );
      toast.success(`Marked ${rsvp}`);
      // If accepted, immediately offer calendar options
      if (rsvp === "accepted") {
        try {
          const { data } = await api.get(
            `/group-dining/${encodeURIComponent(gid)}/calendar-links${apiParams(t, a)}`,
          );
          setCalLinks(data);
        } catch { /* silent */ }
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const acceptAndSignup = () => {
    // Save invite context in sessionStorage so the signup page can
    // finish the RSVP + upgrade the shadow account on completion.
    try {
      sessionStorage.setItem(
        "hp_pending_group_signup",
        JSON.stringify({
          gid,
          t: t || "",
          a: a || "",
          email: state.data?.me?.invitee_email || "",
          name: state.data?.me?.invitee_name || "",
          phone: state.data?.me?.invitee_phone || "",
        }),
      );
    } catch { /* silent */ }
    // Encourage sign-up on the register page
    window.location.href = "/register?from=group-invite";
  };

  const submitChat = async () => {
    if (!chatDraft.trim()) return;
    try {
      await api.post(
        `/group-dining/${encodeURIComponent(gid)}/chat${apiParams(t, a)}`,
        { message: chatDraft.trim() },
      );
      setChatDraft("");
      loadChat();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const flagLate = async (minutes) => {
    const me = state.data?.me;
    if (!me) return;
    try {
      await api.post(
        `/group-dining/${encodeURIComponent(gid)}/late${apiParams(t, a)}`,
        { attendee_id: me.id, minutes },
      );
      toast.success(`Told the host you'll be ${minutes} min late`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const shareLocation = async () => {
    const me = state.data?.me;
    if (!me) return;
    if (!navigator.geolocation) {
      toast.error("This browser can't share location.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.post(
            `/group-dining/${encodeURIComponent(gid)}/location${apiParams(t, a)}`,
            {
              attendee_id: me.id,
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            },
          );
          if (data.eta_minutes != null) {
            toast.success(`ETA ~${data.eta_minutes} min`);
          } else {
            toast.success("Location shared");
          }
          loadMap();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
      },
      () => toast.error("Location permission denied"),
      { timeout: 8000 },
    );
  };

  const dietaryBadges = useMemo(() => {
    const s = state.data?.dietary_summary;
    if (!s) return [];
    const out = [];
    for (const [tag, count] of Object.entries(s.dietary_counts || {})) {
      out.push({ kind: "diet", tag, count });
    }
    for (const [tag, count] of Object.entries(s.allergy_counts || {})) {
      out.push({ kind: "allergy", tag, count });
    }
    return out;
  }, [state.data?.dietary_summary]);

  if (state.loading) {
    return (
      <div
        data-testid="grp-page-loading"
        className="min-h-screen flex items-center justify-center text-slate-500"
      >
        Loading…
      </div>
    );
  }
  if (state.error || !state.data) {
    return (
      <div
        data-testid="grp-page-error"
        className="min-h-screen flex items-center justify-center p-6"
      >
        <div className="max-w-md w-full bg-white border border-rose-200 rounded-xl p-6">
          <h1 className="text-lg font-semibold text-rose-700 mb-1">
            Can&apos;t open this invite
          </h1>
          <p className="text-sm text-slate-600">{state.error || "Not found"}</p>
        </div>
      </div>
    );
  }

  const g = state.data.group;
  const me = state.data.me;
  const attendees = state.data.attendees || [];
  const showRsvp = !me.is_host && me.rsvp !== "accepted" && me.rsvp !== "declined";

  return (
    <div data-testid="grp-page" className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="text-xs text-slate-500 uppercase tracking-wide">
            {me.is_host ? "You're hosting" : `${g.host_name} invited you`}
          </div>
          <h1
            data-testid="grp-title"
            className="text-2xl font-semibold text-slate-900 mt-1"
          >
            Dinner at {g.business_name || g.business_slug || "the restaurant"}
          </h1>
          <div className="text-sm text-slate-600 mt-1">
            <Icons.Calendar
              size={14}
              className="inline -mt-0.5 mr-1"
            />
            {fmtTime(g.booking_time_iso)} · party of {g.party_size}
          </div>
          {g.notes && (
            <div className="mt-2 text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
              {g.notes}
            </div>
          )}
          {dietaryBadges.length > 0 && (
            <div
              data-testid="grp-dietary-badges"
              className="mt-3 flex flex-wrap gap-1"
            >
              {dietaryBadges.map((b, i) => (
                <span
                  key={i}
                  className={`text-xs px-1.5 py-0.5 rounded border ${
                    b.kind === "allergy"
                      ? "bg-rose-50 border-rose-200 text-rose-800"
                      : "bg-emerald-50 border-emerald-200 text-emerald-800"
                  }`}
                >
                  {b.kind === "allergy" ? "⚠ " : ""}
                  {b.tag} ×{b.count}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* RSVP */}
        {showRsvp && (
          <div
            data-testid="grp-rsvp-block"
            className="bg-white border border-slate-200 rounded-2xl p-6"
          >
            <h2 className="text-sm font-semibold text-slate-700 mb-3">
              Are you in?
            </h2>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="grp-rsvp-accept-btn"
                onClick={() => submitRsvp("accepted")}
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-40"
              >
                Yes, I&apos;m in
              </button>
              {/* Non-members get the extra "Accept & sign up" CTA which
                  upgrades their shadow account and unlocks pre-orders. */}
              {me.is_shadow_at_invite && (
                <button
                  data-testid="grp-rsvp-accept-signup-btn"
                  onClick={acceptAndSignup}
                  className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium"
                >
                  Accept + sign up (unlocks pre-order)
                </button>
              )}
              <button
                data-testid="grp-rsvp-maybe-btn"
                onClick={() => submitRsvp("maybe")}
                disabled={busy}
                className="px-4 py-2 rounded-lg border border-slate-200 text-sm"
              >
                Maybe
              </button>
              <button
                data-testid="grp-rsvp-decline-btn"
                onClick={() => submitRsvp("declined")}
                disabled={busy}
                className="px-4 py-2 rounded-lg border border-rose-200 text-rose-700 text-sm"
              >
                Can&apos;t make it
              </button>
            </div>
          </div>
        )}

        {/* Calendar links (shown after accept, or any time for
            already-accepted attendees) */}
        {(calLinks || me.rsvp === "accepted" || me.is_host) && (
          <div
            data-testid="grp-calendar-block"
            className="bg-white border border-slate-200 rounded-2xl p-6"
          >
            <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
              <Icons.CalendarPlus size={14} />
              Add to your calendar
            </h2>
            <CalendarButtons gid={gid} t={t} a={a} preloaded={calLinks} />
          </div>
        )}

        {/* Attendee list */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">
            Who&apos;s coming ({attendees.filter((x) => x.rsvp === "accepted" || x.is_host).length}/{attendees.length})
          </h2>
          <ul data-testid="grp-attendees" className="divide-y divide-slate-100">
            {attendees.map((att) => (
              <li
                key={att.id}
                data-testid={`grp-attendee-${att.id}`}
                className="py-2 flex items-center justify-between"
              >
                <div>
                  <span className="text-slate-800">{att.invitee_name}</span>
                  {att.is_host && (
                    <span className="ml-2 text-xs text-slate-400">host</span>
                  )}
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    att.rsvp === "accepted" || att.is_host
                      ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                      : att.rsvp === "declined"
                      ? "bg-rose-50 text-rose-700 border border-rose-200"
                      : att.rsvp === "maybe"
                      ? "bg-amber-50 text-amber-800 border border-amber-200"
                      : "bg-slate-50 text-slate-500 border border-slate-200"
                  }`}
                >
                  {att.is_host ? "hosting" : att.rsvp || "invited"}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Pre-order menu (members only — one of the reasons to sign up) */}
        {me.rsvp === "accepted" && !me.is_host && (
          <div
            data-testid="grp-preorder-block"
            className="bg-white border border-slate-200 rounded-2xl p-6"
          >
            <h2 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
              <Icons.UtensilsCrossed size={14} />
              Pre-order your dishes
            </h2>
            {me.is_shadow_at_invite ? (
              <div>
                <p className="text-sm text-slate-600 mb-3">
                  Pre-ordering is for members only — one of the reasons
                  to sign up. Peking Duck, live lobster, and other
                  advance-prep dishes need to be locked in before you
                  arrive. Sign up now and you can lock your dishes in
                  seconds.
                </p>
                <button
                  data-testid="grp-preorder-signup-btn"
                  onClick={acceptAndSignup}
                  className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm"
                >
                  Sign up + unlock pre-order
                </button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-600 mb-3">
                  Get a head start on the menu — pre-order regular
                  items (changeable until you arrive), or lock in
                  advance-prep dishes with a small prepayment.
                </p>
                <a
                  data-testid="grp-preorder-open-btn"
                  href={`/group-dining/${gid}/menu${apiParams(t, a)}`}
                  className="inline-block px-4 py-2 rounded-lg bg-slate-900 text-white text-sm"
                >
                  Open menu →
                </a>
              </div>
            )}
          </div>
        )}

        {/* Live map */}
        {state.data.live_map_open && (
          <div
            data-testid="grp-live-map"
            className="bg-white border border-slate-200 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                <Icons.MapPin size={14} />
                Live map — everyone on their way
              </h2>
              <button
                data-testid="grp-share-loc-btn"
                onClick={shareLocation}
                className="text-xs px-2.5 py-1 rounded-lg bg-slate-900 text-white"
              >
                Share my location
              </button>
            </div>
            {mapData?.pins?.length ? (
              <ul className="divide-y divide-slate-100 text-sm">
                {mapData.pins.map((p) => (
                  <li
                    key={p.attendee_id}
                    className="py-2 flex items-center justify-between"
                  >
                    <span className="text-slate-800">{p.name}</span>
                    <span className="text-slate-500 text-xs">
                      {p.arrived
                        ? "✅ arrived"
                        : p.running_late_min
                        ? `⏰ ${p.running_late_min} min late`
                        : p.eta_minutes != null
                        ? `~${p.eta_minutes} min away`
                        : "sharing…"}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-slate-400 text-sm">
                No live positions yet — tap “Share my location” to be first.
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <button
                data-testid="grp-late-15-btn"
                onClick={() => flagLate(15)}
                className="text-xs px-2 py-1 rounded-lg border border-amber-200 bg-amber-50 text-amber-800"
              >
                I&apos;ll be 15 min late
              </button>
              <button
                data-testid="grp-late-30-btn"
                onClick={() => flagLate(30)}
                className="text-xs px-2 py-1 rounded-lg border border-amber-200 bg-amber-50 text-amber-800"
              >
                30 min
              </button>
            </div>
          </div>
        )}

        {/* Chat */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">
            Group chat
          </h2>
          <div
            ref={chatBoxRef}
            data-testid="grp-chat-box"
            className="max-h-64 overflow-y-auto space-y-2 mb-3 bg-slate-50 border border-slate-100 rounded-lg p-3"
          >
            {chat.length === 0 && (
              <div className="text-slate-400 text-xs">
                Nothing yet — kick it off.
              </div>
            )}
            {chat.map((m) => (
              <div key={m.id} className="text-sm">
                <span className="font-semibold text-slate-700">
                  {m.sender_name}:
                </span>{" "}
                <span className="text-slate-800">{m.message}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              data-testid="grp-chat-input"
              value={chatDraft}
              onChange={(e) => setChatDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitChat()}
              placeholder="Say something…"
              className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
            <button
              data-testid="grp-chat-send-btn"
              onClick={submitChat}
              className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function CalendarButtons({ gid, t, a, preloaded }) {
  const [links, setLinks] = useState(preloaded || null);

  useEffect(() => {
    if (links) return;
    const q = [];
    if (t) q.push(`t=${encodeURIComponent(t)}`);
    if (a) q.push(`a=${encodeURIComponent(a)}`);
    const qs = q.length ? `?${q.join("&")}` : "";
    api
      .get(`/group-dining/${encodeURIComponent(gid)}/calendar-links${qs}`)
      .then(({ data }) => setLinks(data))
      .catch(() => {});
  }, [gid, t, a, links]);

  if (!links) {
    return <span className="text-xs text-slate-400">Loading links…</span>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      <a
        data-testid="grp-cal-google"
        href={links.google_url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
      >
        Google Calendar
      </a>
      <a
        data-testid="grp-cal-outlook"
        href={links.outlook_url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
      >
        Outlook
      </a>
      <a
        data-testid="grp-cal-ics"
        href={links.ics_url}
        className="text-sm px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
      >
        Apple / iCal (.ics)
      </a>
    </div>
  );
}
